import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Formik } from "formik";
import { connect } from "react-redux";
import { FormattedMessage, injectIntl } from "react-intl";
import { LockClosedIcon, EnvelopeIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";
import * as auth from "../../store/ducks/auth.duck";
import { login, initiateGoogleSSO } from "../../crud/auth.crud";
import TwoFactorVerify from "./TwoFactorVerify";
import TwoFactorEnroll from "./TwoFactorEnroll";
import axios from "axios";

const json = require("../../../config.json");
const urlBase = json.urlBase;

function Login(props) {
  const { intl } = props;
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  // Two-factor flow: held only in memory, never persisted.
  const [mfaStep, setMfaStep] = useState("none"); // 'none' | 'verify' | 'enroll'
  const [tempToken, setTempToken] = useState("");

  // Centralized post-authentication handling, shared by password login, the
  // 2FA verify/enroll steps, and the Google SSO callback.
  const handleAuthenticated = (result) => {
    const normalizedUserDetails = {
      ...result,
      pk: result?.pk ?? result?.user_id ?? result?.id,
      account_id:
        result?.account_id ??
        result?.account?.pk ??
        result?.account?.id ??
        result?.account_pk,
      access_token: result?.access_token,
    };

    try {
      localStorage.setItem("userDetails", JSON.stringify(normalizedUserDetails));
    } catch (storageError) {
      console.warn("Unable to persist user details:", storageError);
    }

    props.login(result.access_token);
    props.fulfillUser(normalizedUserDetails);
  };

  const resetToLogin = () => {
    setMfaStep("none");
    setTempToken("");
    setErrorMessage("");
  };

  // Handle SSO callback — extract token from URL and auto-login
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const error = params.get("error");
    const errorDescription = params.get("error_description");
    // SSO now hands back an MFA-pending token instead of a session token.
    const ssoMfaRequired = params.get("requires_mfa");
    const ssoTempToken = params.get("temp_token");
    const ssoSetupRequired = params.get("mfa_setup_required");

    if (error) {
      setErrorMessage(errorDescription || "Google sign-in failed. Please try again.");
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // SSO verified identity, but 2FA is mandatory. Route into the same TOTP
    // flow as password login instead of logging in directly. Never call
    // /auth/refresh or handleAuthenticated here — the access token is only
    // issued after the second factor by TwoFactorVerify/TwoFactorEnroll.
    if (ssoMfaRequired && ssoTempToken && !isAutoLogin) {
      setIsAutoLogin(true);
      setTempToken(ssoTempToken);
      setMfaStep(ssoSetupRequired === "1" ? "enroll" : "verify");
      // Clean URL so the temp token doesn't linger in the address bar/history.
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (token && !isAutoLogin) {
      setIsAutoLogin(true);
      setLoading(true);

      // Call refresh endpoint with the SSO token to get full user data
      axios
        .get(urlBase + "/auth/refresh", {
          headers: { Authorization: "Bearer " + token },
        })
        .then(({ data: { result } }) => {
          handleAuthenticated(result);
        })
        .catch(() => {
          setLoading(false);
          setIsAutoLogin(false);
          setErrorMessage("Google sign-in failed. Please try again.");
        });

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enableLoading = () => {
    setLoading(true);
    setErrorMessage("");
  };

  const disableLoading = () => {
    setLoading(false);
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-flex tw-overflow-hidden">
      {/* Left Side - Background Image with Logo and Text */}
      <div
        className="tw-hidden lg:tw-flex tw-flex-col tw-justify-between tw-w-1/2 tw-bg-cover tw-bg-center tw-bg-no-repeat tw-p-12 tw-relative"
        style={{
          backgroundImage: 'url(/media/bg/logindsa.jpg)',
        }}
      >
        {/* Dark overlay */}
        <div className="tw-absolute tw-inset-0 tw-bg-black/40"></div>

        {/* Content */}
        <div className="tw-relative tw-z-10">
          <img
            src="/media/logos/logo-4.png"
            alt="ITP Logo"
            style={{ width: '100px' }}
            className="tw-mb-8"
          />
        </div>

        <div className="tw-relative tw-z-10">
          <h3 className="tw-text-3xl tw-font-bold tw-text-white tw-mb-4">
            Welcome to ITP!
          </h3>
          <p className="tw-text-white/90 tw-text-lg tw-leading-relaxed">
            By logging in you agree to follow all ITP company policies and procedures. Your activity may be monitored.
          </p>
        </div>

        <div className="tw-relative tw-z-10">
          <p className="tw-text-white/80 tw-text-sm">
            ITP © {new Date().getFullYear()}
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="tw-w-full lg:tw-w-1/2 tw-flex tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-blue-600 tw-to-indigo-700 dark:tw-from-gray-900 dark:tw-to-gray-800 tw-overflow-auto">
        <div className="tw-max-w-md tw-w-full tw-px-8 tw-py-12">
          {/* Logo - always visible */}
          <div className="tw-text-center tw-mb-8">
            <img
              src="/media/logos/logo-4.png"
              alt="ITP Logo"
              style={{ width: '100px' }}
              className="tw-mx-auto tw-mb-4"
            />
            <p className="tw-mt-2 tw-text-sm tw-text-white/90 dark:tw-text-gray-300">
              Sign in to your account
            </p>
          </div>

          {/* Login Card */}
          <div className="tw-bg-white dark:tw-bg-gray-800 tw-rounded-2xl tw-shadow-xl tw-p-8">
            {mfaStep === "verify" ? (
              <TwoFactorVerify
                tempToken={tempToken}
                onAuthenticated={handleAuthenticated}
                onCancel={resetToLogin}
              />
            ) : mfaStep === "enroll" ? (
              <TwoFactorEnroll
                tempToken={tempToken}
                onAuthenticated={handleAuthenticated}
                onCancel={resetToLogin}
              />
            ) : (
            <Formik
              initialValues={{
                email: "",
                password: "",
              }}
              validate={(values) => {
                const errors = {};

                if (!values.email) {
                  errors.email = intl.formatMessage({
                    id: "AUTH.VALIDATION.REQUIRED_FIELD",
                  });
                } else if (
                  !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
                ) {
                  errors.email = intl.formatMessage({
                    id: "AUTH.VALIDATION.INVALID_FIELD",
                  });
                }

                if (!values.password) {
                  errors.password = intl.formatMessage({
                    id: "AUTH.VALIDATION.REQUIRED_FIELD",
                  });
                }

                return errors;
              }}
              onSubmit={(values, { setStatus, setSubmitting }) => {
                enableLoading();
                setTimeout(() => {
                  login(values.email, values.password)
                    .then(({ data: { result } }) => {
                      disableLoading();
                      setSubmitting(false);

                      // Password verified. 2FA is mandatory: the backend never
                      // returns a session token here, only an MFA-pending token
                      // plus whether the user still needs to enroll.
                      if (result && result.requires_mfa) {
                        setTempToken(result.temp_token);
                        setMfaStep(result.mfa_setup_required ? "enroll" : "verify");
                        return;
                      }

                      handleAuthenticated(result);
                    })
                    .catch(() => {
                      disableLoading();
                      setSubmitting(false);
                      setErrorMessage("Incorrect email or password. Please try again.");
                    });
                }, 1000);
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit} className="tw-space-y-6">
                  {/* Error Alert */}
                  {errorMessage && (
                    <div className="tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-800 tw-rounded-lg tw-p-4 tw-flex tw-items-start tw-gap-3">
                      <ExclamationCircleIcon className="tw-h-5 tw-w-5 tw-text-red-600 dark:tw-text-red-400 tw-flex-shrink-0 tw-mt-0.5" />
                      <div>
                        <h3 className="tw-text-sm tw-font-medium tw-text-red-800 dark:tw-text-red-300">
                          Authentication Error
                        </h3>
                        <p className="tw-mt-1 tw-text-sm tw-text-red-700 dark:tw-text-red-400">
                          {errorMessage}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Email Field */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Email Address
                    </label>
                    <div className="tw-relative">
                      <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-pl-3 tw-flex tw-items-center tw-pointer-events-none">
                        <EnvelopeIcon className="tw-h-5 tw-w-5 tw-text-gray-400" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`tw-block tw-w-full tw-pl-10 tw-pr-3 tw-py-3 tw-border tw-rounded-lg tw-bg-white dark:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-white placeholder:tw-text-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-transition-colors ${
                          touched.email && errors.email
                            ? "tw-border-red-300 dark:tw-border-red-700"
                            : "tw-border-gray-300 dark:tw-border-gray-600"
                        }`}
                        placeholder="you@example.com"
                      />
                    </div>
                    {touched.email && errors.email && (
                      <p className="tw-mt-2 tw-text-sm tw-text-red-600 dark:tw-text-red-400">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Password Field */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Password
                    </label>
                    <div className="tw-relative">
                      <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-pl-3 tw-flex tw-items-center tw-pointer-events-none">
                        <LockClosedIcon className="tw-h-5 tw-w-5 tw-text-gray-400" />
                      </div>
                      <input
                        type="password"
                        name="password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`tw-block tw-w-full tw-pl-10 tw-pr-3 tw-py-3 tw-border tw-rounded-lg tw-bg-white dark:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-white placeholder:tw-text-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-transition-colors ${
                          touched.password && errors.password
                            ? "tw-border-red-300 dark:tw-border-red-700"
                            : "tw-border-gray-300 dark:tw-border-gray-600"
                        }`}
                        placeholder="••••••••"
                      />
                    </div>
                    {touched.password && errors.password && (
                      <p className="tw-mt-2 tw-text-sm tw-text-red-600 dark:tw-text-red-400">
                        {errors.password}
                      </p>
                    )}
                  </div>

                  {/* Forgot Password Link */}
                  <div className="tw-flex tw-items-center tw-justify-end">
                    <Link
                      to="/auth/forgot-password"
                      className="tw-text-sm tw-font-medium tw-text-blue-600 hover:tw-text-blue-500 dark:tw-text-blue-400 dark:hover:tw-text-blue-300 tw-transition-colors"
                    >
                      <FormattedMessage id="AUTH.GENERAL.FORGOT_BUTTON" />
                    </Link>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting || loading}
                    className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-font-semibold tw-shadow-lg hover:tw-shadow-xl tw-transition-all disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <svg
                          className="tw-animate-spin tw-h-5 tw-w-5 tw-text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="tw-opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="tw-opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Signing in...</span>
                      </>
                    ) : (
                      <span>Sign In</span>
                    )}
                  </button>

                  {/* Divider */}
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <div className="tw-flex-1 tw-h-px tw-bg-gray-300 dark:tw-bg-gray-600"></div>
                    <span className="tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">or</span>
                    <div className="tw-flex-1 tw-h-px tw-bg-gray-300 dark:tw-bg-gray-600"></div>
                  </div>

                  {/* Google SSO Button */}
                  <button
                    type="button"
                    onClick={() => initiateGoogleSSO()}
                    disabled={loading}
                    className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-3 tw-px-4 tw-py-3 tw-rounded-lg tw-bg-white tw-border tw-border-gray-300 hover:tw-bg-gray-50 tw-text-gray-700 tw-font-medium tw-shadow-sm hover:tw-shadow-md tw-transition-all disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                  >
                    <svg className="tw-h-5 tw-w-5" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    <span>Sign in with Google</span>
                  </button>
                </form>
              )}
            </Formik>
            )}
          </div>

          {/* Footer */}
          <p className="tw-mt-6 tw-text-center tw-text-sm tw-text-white/80 dark:tw-text-gray-400 lg:tw-hidden">
            ITP © {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
}

export default injectIntl(connect(null, auth.actions)(Login));
